import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Flame, Clock, BookOpen, Award, LogOut, Share2, Gift, Trash2, HardDrive, Bell, BellOff, Send, KeyRound, AlertTriangle, Archive, Download } from 'lucide-react';
import api, { API } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import ShareModal from '@/components/ShareModal';
import RecoveryCodeModal from '@/components/RecoveryCodeModal';
import { clearAll, formatBytes, listDownloads, removeItem } from '@/lib/offlineCache';
import { isPushSupported, fetchStatus as fetchPushStatus, subscribe as subscribePush, unsubscribe as unsubscribePush, sendTest as sendPushTest, permission as pushPermission } from '@/lib/push';

function CalendarHeatmap({ data }) {
    if (!data?.length) return null;
    const max = Math.max(1, ...data.map((d) => d.minutes));
    // 13 weeks of 7 days = 91; we have 90 — pad start
    const cells = data.slice(); // already 90, oldest first
    return (
        <div className="overflow-x-auto" data-testid="calendar-heatmap">
            <div className="grid grid-rows-7 grid-flow-col gap-1 min-w-[640px]">
                {cells.map((d) => {
                    const intensity = d.minutes / max;
                    const bg = d.minutes === 0
                        ? 'rgba(45,74,62,0.07)'
                        : `rgba(122,163,134,${0.25 + intensity * 0.7})`;
                    return (
                        <div
                            key={d.date}
                            title={`${d.date} — ${d.minutes} min`}
                            className="w-3.5 h-3.5 rounded-md transition-transform hover:scale-110"
                            style={{ backgroundColor: bg }}
                        />
                    );
                })}
            </div>
            <div className="flex items-center gap-2 text-xs text-forest-soft mt-3">
                <span>Less</span>
                {[0.1, 0.3, 0.55, 0.8, 1].map((p) => (
                    <span key={p} className="w-3 h-3 rounded-sm" style={{ backgroundColor: `rgba(122,163,134,${0.2 + p * 0.7})` }} />
                ))}
                <span>More</span>
                <span className="ml-auto">Last 90 days</span>
            </div>
        </div>
    );
}

export default function Profile() {
    const { user, logout, refresh: refreshUser, setUser } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [moods, setMoods] = useState([]);
    const [share, setShare] = useState(null);
    const [premium, setPremium] = useState(null);
    const [redeemCode, setRedeemCode] = useState('');
    const [redeemMsg, setRedeemMsg] = useState(null);
    const [redeeming, setRedeeming] = useState(false);
    const [downloads, setDownloads] = useState({ items: [], total_bytes: 0 });
    const [pushState, setPushState] = useState({ enabled: false, devices: 0, permission: 'default', supported: isPushSupported() });
    const [pushBusy, setPushBusy] = useState(false);
    const [pushMsg, setPushMsg] = useState(null);
    const [tzEditing, setTzEditing] = useState(false);
    const [tzValue, setTzValue] = useState('');
    const [newRecoveryCode, setNewRecoveryCode] = useState(null);
    const [regenBusy, setRegenBusy] = useState(false);
    const [deleteMode, setDeleteMode] = useState(false);
    const [deletePw, setDeletePw] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [musicExportBusy, setMusicExportBusy] = useState(false);
    const [musicExportError, setMusicExportError] = useState('');

    const [musicExportProgress, setMusicExportProgress] = useState('');

    // Server builds the ZIP on disk; the browser then downloads it natively (with size check).
    const downloadMusicArchive = async () => {
        setMusicExportBusy(true);
        setMusicExportError('');
        try {
            let { data } = await api.post('/admin/music/export');
            while (data.state === 'building') {
                setMusicExportProgress(data.total ? `Preparing ${data.done}/${data.total}…` : 'Preparing…');
                await new Promise((r) => setTimeout(r, 2000));
                ({ data } = await api.get('/admin/music/export/status'));
            }
            if (data.state !== 'ready') throw new Error(data.error || 'Build failed');
            window.location.assign(`${API}${data.download_url.replace(/^\/api/, "")}`);
        } catch (err) {
            setMusicExportError(err?.response?.data?.detail || 'Could not prepare the archive. Please try again.');
        } finally {
            setMusicExportBusy(false);
            setMusicExportProgress('');
        }
    };

    const regenerateRecoveryCode = async () => {
        if (!window.confirm('Generate a new recovery code? Your old code will stop working.')) return;
        setRegenBusy(true);
        try {
            const { data } = await api.post('/auth/reset/regenerate-code');
            setNewRecoveryCode(data.recovery_code);
        } catch {} finally { setRegenBusy(false); }
    };

    const submitDelete = async (e) => {
        e.preventDefault();
        setDeleteError('');
        setDeleteBusy(true);
        try {
            await api.post('/auth/delete-account', { password: deletePw });
            // Soft-delete succeeded — sign user out locally; signing back in
            // within 30 days restores their account.
            logout();
            navigate('/', { replace: true });
        } catch (err) {
            setDeleteError(err?.response?.data?.detail || 'Could not delete account.');
        } finally {
            setDeleteBusy(false);
        }
    };

    const refreshDownloads = async () => setDownloads(await listDownloads());
    const refreshGifts = async () => {
        try { const r = await api.get('/gifts/mine'); setPremium(r.data); } catch {}
    };
    const refreshPush = async () => {
        const s = await fetchPushStatus();
        setPushState((p) => ({ ...p, enabled: s.enabled, devices: s.devices, permission: pushPermission() }));
    };

    useEffect(() => {
        api.get('/sessions/stats').then((r) => setStats(r.data)).catch(() => {});
        api.get('/mood/recent', { params: { limit: 14 } }).then((r) => setMoods(r.data || [])).catch(() => {});
        refreshGifts();
        refreshDownloads();
        refreshPush();
    }, []);

    useEffect(() => {
        setTzValue(user?.onboarding?.timezone || (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC'));
    }, [user]);

    const togglePush = async () => {
        setPushBusy(true); setPushMsg(null);
        try {
            if (pushState.enabled) {
                await unsubscribePush();
                setPushMsg({ ok: true, text: 'Gentle reminders turned off on this device.' });
            } else {
                await subscribePush();
                setPushMsg({ ok: true, text: 'Reminders enabled. We will whisper at your chosen hour.' });
            }
            await refreshPush();
        } catch (e) {
            setPushMsg({ ok: false, text: e?.message || 'Could not toggle reminders.' });
        } finally { setPushBusy(false); }
    };

    const testPush = async () => {
        setPushBusy(true); setPushMsg(null);
        try {
            const r = await sendPushTest();
            setPushMsg({ ok: r.delivered > 0, text: r.delivered > 0 ? `Sent to ${r.delivered} device${r.delivered === 1 ? '' : 's'}.` : 'No device received it. Try re-enabling.' });
        } catch (e) {
            setPushMsg({ ok: false, text: e?.response?.data?.detail || 'Test failed.' });
        } finally { setPushBusy(false); }
    };

    const saveTimezone = async () => {
        try {
            await api.put('/me/timezone', { timezone: tzValue });
            // Optimistic update so the UI reflects the new value immediately
            // (the SW used to serve stale /api/auth/me — fixed in sw.js, but
            // this keeps the UI snappy even on slow networks).
            setUser({
                ...(user || {}),
                onboarding: { ...(user?.onboarding || {}), timezone: tzValue },
            });
            try { await refreshUser(); } catch {}
            setTzEditing(false);
            setPushMsg({ ok: true, text: `Timezone set to ${tzValue}.` });
        } catch {
            setPushMsg({ ok: false, text: 'Could not save timezone.' });
        }
    };

    const redeem = async (e) => {
        e.preventDefault();
        setRedeeming(true); setRedeemMsg(null);
        try {
            const { data } = await api.post('/gifts/redeem', { code: redeemCode });
            setRedeemMsg({ ok: true, text: `Unlocked ${data.months_added} months of premium 🌿` });
            setRedeemCode('');
            refreshGifts();
        } catch (err) {
            setRedeemMsg({ ok: false, text: err?.response?.data?.detail || 'Could not redeem this code.' });
        } finally { setRedeeming(false); }
    };

    const dropDownload = async (u) => {
        await removeItem(u);
        refreshDownloads();
    };
    const dropAll = async () => {
        if (!window.confirm('Remove all downloaded audio?')) return;
        await clearAll();
        refreshDownloads();
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const shareStreak = () => {
        if (!stats) return;
        setShare({
            card: 'streak',
            days: stats.current_streak,
            name: user?.name?.split(' ')[0],
        });
    };
    const shareMinutes = () => {
        if (!stats) return;
        setShare({
            card: 'minutes',
            minutes: Math.round(stats.total_minutes),
            sessions: stats.total_sessions,
            name: user?.name?.split(' ')[0],
        });
    };

    return (
        <div className="space-y-8" data-testid="profile-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-forest-soft">You</p>
                    <h1 className="font-serif text-4xl md:text-5xl text-forest mt-2">{user?.name}</h1>
                    <p className="text-forest-soft mt-1">{user?.email}</p>
                </div>
                <button onClick={handleLogout} data-testid="profile-logout-button" className="qm-btn-ghost inline-flex items-center gap-2 self-start">
                    <LogOut size={16} /> Sign out
                </button>
            </header>

            <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="profile-stats">
                <BigStat icon={<Flame size={18} />} label="Current streak" value={stats ? `${stats.current_streak}` : '—'} suffix="days" onShare={stats && stats.current_streak > 0 ? shareStreak : undefined} testId="share-streak" />
                <BigStat icon={<Award size={18} />} label="Longest streak" value={stats ? `${stats.longest_streak}` : '—'} suffix="days" />
                <BigStat icon={<Clock size={18} />} label="Total time" value={stats ? `${Math.round(stats.total_minutes)}` : '—'} suffix="min" onShare={stats && stats.total_minutes > 0 ? shareMinutes : undefined} testId="share-minutes" />
                <BigStat icon={<BookOpen size={18} />} label="Sessions" value={stats ? `${stats.total_sessions}` : '—'} suffix="" />
            </section>

            <section className="qm-card p-7 md:p-9" data-testid="profile-heatmap-section">
                <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
                    <div>
                        <h2 className="font-serif text-2xl text-forest">Your quiet days</h2>
                        <p className="text-sm text-forest-soft">Each square is a day. Greener squares = more minutes.</p>
                    </div>
                </div>
                {stats && <CalendarHeatmap data={stats.heatmap} />}
            </section>

            <section className="qm-card p-7 md:p-9" data-testid="profile-achievements">
                <h2 className="font-serif text-2xl text-forest mb-4">Small honors</h2>
                {stats?.achievements?.length ? (
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {stats.achievements.map((a) => (
                            <li key={a.id} className="rounded-2xl border border-border p-4 flex items-center gap-3 bg-sage/15">
                                <div className="w-10 h-10 rounded-full bg-forest grid place-items-center text-cream"><Award size={18} /></div>
                                <div className="font-medium text-forest">{a.title}</div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-forest-soft text-sm">Your first badge will arrive after your first session. No rush.</p>
                )}
            </section>

            {moods.length > 0 && (
                <section className="qm-card p-7 md:p-9" data-testid="profile-recent-moods">
                    <h2 className="font-serif text-2xl text-forest mb-4">Recent moods</h2>
                    <div className="flex flex-wrap gap-2">
                        {moods.map((m) => (
                            <span key={m.id} className="qm-pill bg-dusty/30 border-transparent text-sm">
                                {m.mood} <span className="text-xs text-forest-soft ml-1">{new Date(m.created_at).toLocaleDateString()}</span>
                            </span>
                        ))}
                    </div>
                </section>
            )}

            <section className="qm-card p-7 md:p-9" data-testid="profile-preferences">
                <h2 className="font-serif text-2xl text-forest mb-4">Your preferences</h2>
                {user?.onboarding ? (
                    <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                        <div className="flex justify-between border-b border-border/60 pb-2"><dt className="text-forest-soft">Experience</dt><dd className="text-forest">{user.onboarding.experience_level}</dd></div>
                        <div className="flex justify-between border-b border-border/60 pb-2"><dt className="text-forest-soft">Session length</dt><dd className="text-forest">{user.onboarding.session_length} min</dd></div>
                        <div className="flex justify-between border-b border-border/60 pb-2"><dt className="text-forest-soft">Reminder</dt><dd className="text-forest">{user.onboarding.reminder_time || '—'}</dd></div>
                        <div className="flex justify-between border-b border-border/60 pb-2"><dt className="text-forest-soft">Focus</dt><dd className="text-forest">{(user.onboarding.focus_areas || []).join(', ') || '—'}</dd></div>
                        <div className="flex justify-between border-b border-border/60 pb-2 sm:col-span-2"><dt className="text-forest-soft">Goals</dt><dd className="text-forest">{(user.onboarding.goals || []).join(' · ') || '—'}</dd></div>
                    </dl>
                ) : (
                    <p className="text-forest-soft">No preferences saved yet.</p>
                )}
            </section>

            {share && <ShareModal data={share} onClose={() => setShare(null)} />}
            {newRecoveryCode && (
                <RecoveryCodeModal
                    code={newRecoveryCode}
                    email={user?.email}
                    onClose={() => setNewRecoveryCode(null)}
                    title="Save your new recovery code"
                    intro="Your old code is now invalid. Please save this new one — we won’t show it again."
                />
            )}

            {/* Security */}
            <section className="qm-card p-7 md:p-9" data-testid="profile-security">
                <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <h2 className="font-serif text-2xl text-forest flex items-center gap-2"><KeyRound size={20} /> Security</h2>
                        <p className="text-sm text-forest-soft">
                            Your recovery code is the only way to reset a forgotten password. Generate a new one anytime — the old one will stop working immediately.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={regenerateRecoveryCode}
                        disabled={regenBusy}
                        data-testid="regenerate-recovery-button"
                        className="qm-btn-ghost text-sm disabled:opacity-60"
                    >
                        {regenBusy ? 'Generating…' : 'Generate new recovery code'}
                    </button>
                </div>

                <div className="border-t border-border/60 mt-6 pt-6" data-testid="music-export-zone">
                    <div className="flex items-end justify-between flex-wrap gap-2">
                        <div>
                            <h3 className="font-serif text-lg text-forest flex items-center gap-2">
                                <Archive size={16} /> Export music library
                            </h3>
                            <p className="text-sm text-forest-soft mt-0.5">
                                Download a ZIP containing every ambient track plus the catalog JSON. Useful as a cold backup. The file is large (about 1 GB) and may take a minute to build, then downloads through your browser&rsquo;s normal download manager.
                            </p>
                            {musicExportError && (
                                <p className="text-sm text-red-700 mt-2" data-testid="music-export-error">{musicExportError}</p>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={downloadMusicArchive}
                            disabled={musicExportBusy}
                            data-testid="music-export-button"
                            className="qm-btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                        >
                            <Download size={14} /> {musicExportBusy ? (musicExportProgress || 'Preparing…') : 'Download ZIP'}
                        </button>
                    </div>
                </div>

                <div className="border-t border-border/60 mt-6 pt-6" data-testid="danger-zone">
                    <div className="flex items-end justify-between flex-wrap gap-2">
                        <div>
                            <h3 className="font-serif text-lg text-forest flex items-center gap-2 text-red-700">
                                <AlertTriangle size={16} /> Delete account
                            </h3>
                            <p className="text-sm text-forest-soft mt-0.5">
                                Your account enters a 30-day soft-delete. Signing back in within that window restores everything.
                            </p>
                        </div>
                        {!deleteMode && (
                            <button
                                type="button"
                                onClick={() => setDeleteMode(true)}
                                data-testid="delete-account-open"
                                className="qm-btn-ghost text-sm text-red-700"
                            >
                                Delete my account
                            </button>
                        )}
                    </div>

                    {deleteMode && (
                        <form onSubmit={submitDelete} className="mt-4 space-y-3" data-testid="delete-account-form">
                            <label className="text-sm text-forest-soft" htmlFor="del-pw">
                                Confirm your password to continue
                            </label>
                            <input
                                id="del-pw"
                                type="password"
                                value={deletePw}
                                onChange={(e) => setDeletePw(e.target.value)}
                                placeholder="Your current password"
                                className="qm-input w-full"
                                data-testid="delete-account-password"
                                required
                            />
                            {deleteError && (
                                <p className="text-sm text-red-700" data-testid="delete-account-error">{deleteError}</p>
                            )}
                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    disabled={deleteBusy || !deletePw}
                                    data-testid="delete-account-confirm"
                                    className="qm-btn-primary bg-red-700 hover:bg-red-800 disabled:opacity-60"
                                >
                                    {deleteBusy ? 'Deleting…' : 'Yes, delete my account'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setDeleteMode(false); setDeletePw(''); setDeleteError(''); }}
                                    data-testid="delete-account-cancel"
                                    className="qm-btn-ghost"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </section>

            {/* Gentle reminders (Web Push) */}
            <section className="qm-card p-7 md:p-9" data-testid="profile-reminders">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <h2 className="font-serif text-2xl text-forest flex items-center gap-2"><Bell size={20} /> Gentle reminders</h2>
                        <p className="text-sm text-forest-soft">
                            A soft nudge at your reminder time. Lives in the browser — nothing is shared.
                        </p>
                    </div>
                    {pushState.supported ? (
                        <button
                            type="button"
                            onClick={togglePush}
                            disabled={pushBusy || pushState.permission === 'denied'}
                            data-testid={pushState.enabled ? 'push-disable' : 'push-enable'}
                            className={`${pushState.enabled ? 'qm-btn-ghost' : 'qm-btn-primary'} inline-flex items-center gap-2 disabled:opacity-60`}
                        >
                            {pushState.enabled ? <><BellOff size={14} /> Turn off</> : <><Bell size={14} /> Enable on this device</>}
                        </button>
                    ) : (
                        <span className="text-xs text-forest-soft">Not supported in this browser</span>
                    )}
                </div>

                <div className="text-sm text-forest-soft grid sm:grid-cols-2 gap-x-8 gap-y-2" data-testid="reminders-meta">
                    <div className="flex justify-between border-b border-border/60 pb-1">
                        <span>Reminder time</span>
                        <span className="text-forest" data-testid="reminders-time">{user?.onboarding?.reminder_time || '—'}</span>
                    </div>
                    <div className="flex justify-between border-b border-border/60 pb-1">
                        <span>Devices</span>
                        <span className="text-forest" data-testid="reminders-devices">{pushState.devices}</span>
                    </div>
                    <div className="flex justify-between border-b border-border/60 pb-1 sm:col-span-2">
                        <span>Timezone</span>
                        {tzEditing ? (
                            <span className="flex items-center gap-2">
                                <input
                                    value={tzValue}
                                    onChange={(e) => setTzValue(e.target.value)}
                                    placeholder="e.g. America/New_York"
                                    data-testid="timezone-input"
                                    className="qm-input text-sm py-1 px-2 w-56"
                                />
                                <button type="button" data-testid="timezone-save" onClick={saveTimezone} className="qm-btn-primary text-xs px-3 py-1">Save</button>
                                <button type="button" data-testid="timezone-cancel" onClick={() => setTzEditing(false)} className="qm-btn-ghost text-xs px-2 py-1">Cancel</button>
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                <span className="text-forest" data-testid="timezone-value">{user?.onboarding?.timezone || tzValue}</span>
                                <button type="button" data-testid="timezone-edit" onClick={() => setTzEditing(true)} className="text-xs underline text-forest-soft hover:text-forest">change</button>
                            </span>
                        )}
                    </div>
                </div>

                {pushState.enabled && (
                    <button
                        type="button"
                        onClick={testPush}
                        disabled={pushBusy}
                        data-testid="push-test"
                        className="qm-btn-ghost text-sm inline-flex items-center gap-2 mt-4"
                    >
                        <Send size={14} /> Send a test notification
                    </button>
                )}
                {pushMsg && (
                    <p
                        data-testid={pushMsg.ok ? 'push-msg-ok' : 'push-msg-err'}
                        className={`text-sm mt-3 ${pushMsg.ok ? 'text-forest' : 'text-red-700'}`}
                    >
                        {pushMsg.text}
                    </p>
                )}
                {pushState.permission === 'denied' && (
                    <p className="text-xs text-red-700 mt-3" data-testid="push-permission-blocked">
                        Notifications are blocked for this site. Re-enable them in your browser settings.
                    </p>
                )}
            </section>

            {/* Premium & gifts */}
            <section className="qm-card p-7 md:p-9" data-testid="profile-premium">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <h2 className="font-serif text-2xl text-forest flex items-center gap-2"><Gift size={20} /> Premium & gifts</h2>
                        <p className="text-sm text-forest-soft">Redeem a gift code, or send premium to someone you love.</p>
                    </div>
                    <Link to="/app/gifts" data-testid="profile-go-gifts" className="qm-btn-ghost text-sm">Give a gift</Link>
                </div>

                <div className="text-sm text-forest-soft mb-4" data-testid="premium-status">
                    {premium?.premium && premium?.premium_until ? (
                        <span>Premium active until <span className="text-forest font-medium" data-testid="premium-until">{new Date(premium.premium_until).toLocaleDateString()}</span>.</span>
                    ) : (
                        <span>You don’t have premium yet. Redeem a code below to unlock.</span>
                    )}
                </div>

                <form onSubmit={redeem} className="flex flex-col sm:flex-row gap-2" data-testid="redeem-form">
                    <input
                        type="text"
                        value={redeemCode}
                        onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                        placeholder="QM-XXXX-XXXX"
                        data-testid="redeem-code-input"
                        className="qm-input flex-1 tracking-widest font-mono"
                        required
                    />
                    <button
                        type="submit"
                        disabled={redeeming || !redeemCode.trim()}
                        data-testid="redeem-submit"
                        className="qm-btn-primary disabled:opacity-60"
                    >
                        {redeeming ? 'Redeeming…' : 'Redeem'}
                    </button>
                </form>
                {redeemMsg && (
                    <p
                        data-testid={redeemMsg.ok ? 'redeem-success' : 'redeem-error'}
                        className={`text-sm mt-3 ${redeemMsg.ok ? 'text-forest' : 'text-red-700'}`}
                    >
                        {redeemMsg.text}
                    </p>
                )}

                {premium?.redeemed?.length > 0 && (
                    <div className="mt-5 text-xs text-forest-soft">
                        Previously redeemed: {premium.redeemed.map((g) => g.code).join(', ')}
                    </div>
                )}
            </section>

            {/* Offline downloads */}
            <section className="qm-card p-7 md:p-9" data-testid="profile-downloads">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <h2 className="font-serif text-2xl text-forest flex items-center gap-2"><HardDrive size={20} /> Offline downloads</h2>
                        <p className="text-sm text-forest-soft">
                            {downloads.items.length
                                ? `${downloads.items.length} item${downloads.items.length === 1 ? '' : 's'} · ${formatBytes(downloads.total_bytes)} stored on this device`
                                : 'Nothing here yet. Tap the download icon on any meditation or track to save it for offline.'}
                        </p>
                    </div>
                    {downloads.items.length > 0 && (
                        <button
                            type="button"
                            onClick={dropAll}
                            data-testid="downloads-clear-all"
                            className="qm-btn-ghost text-sm inline-flex items-center gap-2 text-red-700"
                        >
                            <Trash2 size={14} /> Clear all
                        </button>
                    )}
                </div>

                {downloads.items.length > 0 && (
                    <ul className="divide-y divide-border/60" data-testid="downloads-list">
                        {downloads.items.map((d) => (
                            <li key={d.url} className="flex items-center gap-3 py-3" data-testid={`download-item-${d.id}`}>
                                <div className="w-10 h-10 rounded-xl bg-sage/30 shrink-0 overflow-hidden grid place-items-center"
                                     style={d.cover_image ? { backgroundImage: `url(${d.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
                                    {!d.cover_image && <HardDrive size={14} className="text-forest-soft" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-forest font-medium truncate">{d.title}</div>
                                    <div className="text-xs text-forest-soft">
                                        {d.kind?.replace('_', ' ')} · {formatBytes(d.size_bytes)}
                                        {d.duration_minutes ? ` · ${d.duration_minutes} min` : ''}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => dropDownload(d.url)}
                                    data-testid={`download-remove-${d.id}`}
                                    aria-label={`Remove ${d.title}`}
                                    className="rounded-full w-8 h-8 grid place-items-center text-forest-soft hover:bg-dusty/40 hover:text-red-700"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

function BigStat({ icon, label, value, suffix, onShare, testId }) {
    return (
        <div className="qm-card p-5 grain-overlay relative">
            <div className="flex items-center gap-2 text-forest-soft text-sm">{icon}{label}</div>
            <div className="font-serif text-3xl text-forest mt-2 tabular-nums">{value}<span className="text-base text-forest-soft ml-1">{suffix}</span></div>
            {onShare && (
                <button
                    onClick={onShare}
                    aria-label={`Share ${label}`}
                    data-testid={testId}
                    className="absolute top-3 right-3 rounded-full w-8 h-8 grid place-items-center text-forest hover:bg-sage/30 transition"
                >
                    <Share2 size={14} />
                </button>
            )}
        </div>
    );
}
